import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { BRAND_COLORS } from "@/lib/api";
import { TagnPayBadge } from "@/components/common";
import {
  LayoutDashboard, Building2, Package, QrCode, Users, MessageCircle,
  Sparkles, Gamepad2, Star, Megaphone, Banknote, BarChart3, Shield, Settings,
  LogOut, ChevronDown, Smartphone,
} from "lucide-react";

const NAV = [
  { to: "/app/overview", label: "Overview", icon: LayoutDashboard, tid: "overview" },
  { to: "/app/brands", label: "Brands", icon: Building2, tid: "brands" },
  { to: "/app/products", label: "Products / SKUs", icon: Package, tid: "products" },
  { to: "/app/qr", label: "QR Codes", icon: QrCode, tid: "qr-codes" },
  { to: "/app/customers", label: "Customers & Passbook", icon: Users, tid: "customers" },
  { to: "/app/whatsapp", label: "WhatsApp Inbox", icon: MessageCircle, tid: "whatsapp" },
  { to: "/app/schemes", label: "Schemes", icon: Sparkles, tid: "schemes" },
  { to: "/app/games", label: "Games", icon: Gamepad2, tid: "games" },
  { to: "/app/feedback", label: "Feedback", icon: Star, tid: "feedback" },
  { to: "/app/campaigns", label: "Campaigns", icon: Megaphone, tid: "campaigns" },
  { to: "/app/redemptions", label: "Redemptions & Payouts", icon: Banknote, tid: "redemptions" },
  { to: "/app/reports", label: "Reports", icon: BarChart3, tid: "reports" },
  { to: "/app/team", label: "Team & Access", icon: Shield, tid: "team" },
  { to: "/app/settings", label: "Settings", icon: Settings, tid: "settings" },
];

export default function AdminLayout({ children }) {
  const { user, brands, selectedBrand, setSelectedBrand, dateRange, setDateRange, logout } = useApp();
  const nav = useNavigate();

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Sidebar */}
      <aside className="w-[260px] shrink-0 sidebar-dark text-slate-200 flex flex-col fixed h-screen">
        <div className="px-5 py-5 border-b border-white/10">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg tnp-gradient flex items-center justify-center font-bold text-white font-head">S</div>
            <div>
              <div className="font-bold text-white text-sm leading-tight font-head">SUGAR Group Rewards</div>
              <div className="text-[10px] text-violet-300 font-semibold tracking-wide">POWERED BY TAGNPAY</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-0.5">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`admin-sidebar-nav-${n.tid}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                  isActive ? "bg-violet-600 text-white font-semibold" : "text-slate-300 hover:bg-white/5 hover:text-white"
                }`
              }
            >
              <n.icon size={17} />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10">
          <button
            data-testid="open-consumer-simulator-btn"
            onClick={() => nav("/simulator")}
            className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg bg-white/5 hover:bg-white/10 text-sm text-white transition-colors"
          >
            <Smartphone size={16} /> Consumer Simulator
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 ml-[260px] flex flex-col min-h-screen">
        <header className="sticky top-0 z-20 bg-white/90 backdrop-blur border-b border-slate-200 px-6 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="relative">
              <select
                data-testid="admin-header-brand-selector"
                value={selectedBrand}
                onChange={(e) => setSelectedBrand(e.target.value)}
                className="appearance-none bg-white border border-slate-200 rounded-lg pl-7 pr-8 py-2 text-sm font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-violet-200"
              >
                <option value="ALL">All Brands</option>
                {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
              <span className="absolute left-2.5 top-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full" style={{ background: BRAND_COLORS[selectedBrand] || "#6D28D9" }} />
              <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            </div>
            <select
              data-testid="admin-header-date-filter"
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-600 focus:outline-none focus:ring-2 focus:ring-violet-200"
            >
              <option>Today</option><option>7D</option><option>30D</option><option>All</option>
            </select>
          </div>
          <div className="flex items-center gap-3">
            <TagnPayBadge />
            <div className="flex items-center gap-2 pl-3 border-l border-slate-200">
              <div className="w-8 h-8 rounded-full tnp-gradient text-white flex items-center justify-center text-xs font-bold">
                {(user?.name || "U").slice(0, 1)}
              </div>
              <div className="hidden sm:block leading-tight">
                <div className="text-xs font-semibold text-slate-800">{user?.name}</div>
                <div className="text-[10px] text-slate-400">{user?.role}</div>
              </div>
              <button data-testid="logout-btn" onClick={logout} className="ml-1 text-slate-400 hover:text-rose-500 transition-colors">
                <LogOut size={16} />
              </button>
            </div>
          </div>
        </header>
        <main className="flex-1 p-6 lg:p-8 max-w-[1600px] w-full mx-auto">{children}</main>
        <footer className="px-8 py-4 text-center text-xs text-slate-400 border-t border-slate-200">
          SUGAR Cosmetics Group Rewards · Powered by TagnPay · Sandbox environment
        </footer>
      </div>
    </div>
  );
}
